import { bunja } from 'bunja';
import { createScopeFromContext } from 'bunja/react';
import { atom } from 'jotai';
import { createContext } from 'react';

import type { AbstractSocketEvents, SocketState } from '@flipster/socket/AbstractSocket';
import type AbstractSocket from '@flipster/socket/AbstractSocket';
import NullSocket from '@flipster/socket/NullSocket';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import nearWorldClock from '@flipster/utils/clock/near-world-clock';
import { timestampToDate } from '@flipster/utils/datetime';

import type { EventsJsonValue } from '../../model/prex.v2.r230522.events';
import type { ClientMessageJsonValue } from '../../model/prex.v2.r230522.messages.subscription.client';
import type { ServerMessageJsonValue, TablesJsonValue } from '../../model/prex.v2.r230522.messages.subscription.server';
import { weakR230522NormalSubTablesBunja } from './accumulator';
import { weakR230522NormalSubEventsBunja } from './events';
import { weakR230522NormalSubStateBunja } from './subscription';

export type R230522NormalSubSocket = AbstractSocket<ClientMessageJsonValue, R230522NormalSubSocketEvents>;

export interface R230522NormalSubSocketEvents extends AbstractSocketEvents<ServerMessageJsonValue> {
  sessionExpired: void;
  r230522table: TablesJsonValue;
  r230522event: EventsJsonValue;
}

export const nullSocket = new NullSocket<R230522NormalSubSocketEvents>();
nullSocket.connect = () => {
  throw new Error(`A subscription attempt was detected without an R230522 socket being injected.
Please make sure to wrap your component with a <R230522NormalSubContext> and provide an R230522 socket.`);
};

export const R230522NormalSubContext = createContext<R230522NormalSubSocket>(nullSocket);
export const R230522NormalSubScope = createScopeFromContext(R230522NormalSubContext);

// 사용하면 소켓 연결이 시작됨
export const r230522NormalSubSocketBunja = bunja(() => {
  const { r230522NormalSubSocket } = bunja.use(weakR230522NormalSubSocketBunja);
  const { subscribe } = bunja.use(weakR230522NormalSubStateBunja);
  const { tablesAtom } = bunja.use(weakR230522NormalSubTablesBunja);
  const { events } = bunja.use(weakR230522NormalSubEventsBunja);
  const { connect } = bunja.use(r230522NormalSubSocketConnectionBunja);

  bunja.effect(connect);

  return { r230522NormalSubSocket, subscribe, tablesAtom, events };
});
r230522NormalSubSocketBunja.debugLabel = 'r230522NormalSubSocketBunja';

export const r230522NormalSubSocketConnectionBunja = bunja(() => {
  const { r230522NormalSubSocket } = bunja.use(weakR230522NormalSubSocketBunja);
  let count = 0;
  function connect() {
    if (++count === 1) r230522NormalSubSocket.connect();
    return close;
  }
  function close() {
    if (count <= 0) throw new Error('close() called without matching connect()');
    if (--count === 0) r230522NormalSubSocket.close();
  }
  return { connect, close };
});
r230522NormalSubSocketConnectionBunja.debugLabel = 'r230522NormalSubSocketConnectionBunja';

// 소켓 구독을 트리거 하고싶지는 않지만 이벤트는 감지하고 싶을 때 사용
export const weakR230522NormalSubSocketBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);

  const r230522NormalSubSocket = bunja.use(R230522NormalSubScope);
  bunja.effect(() =>
    r230522NormalSubSocket.on('message', (message: ServerMessageJsonValue) => {
      const { t, e } = message;
      const outdatedSkew = getOutdatedMessageSkew(message);
      if (outdatedSkew) {
        return r230522NormalSubSocket.reconnect({
          reason: 'outdated_message',
          extra: {
            diff_ms: outdatedSkew.diffMs,
            server_time_ms: outdatedSkew.serverTimeMs,
            client_time_ms: outdatedSkew.clientTimeMs,
          },
        });
      }
      if (t) r230522NormalSubSocket.emit('r230522table', t);
      if (e) r230522NormalSubSocket.emit('r230522event', e);
    }),
  );

  const socketStateAtom = atom<SocketState>(r230522NormalSubSocket.state);
  const isConnectedAtom = atom<boolean>((get) => get(socketStateAtom) === 'connected');
  bunja.effect(() =>
    r230522NormalSubSocket.on('stateChanged', (state: SocketState) => {
      store.set(socketStateAtom, state);
    }),
  );

  return { r230522NormalSubSocket, socketStateAtom, isConnectedAtom };
});
weakR230522NormalSubSocketBunja.debugLabel = 'weakR230522NormalSubSocketBunja';

// Message clock-skew threshold for discarding the socket. Same number as
// `reconnectLogThrottleWindowMs` by coincidence, not a shared constant.
const outdatedMessageThresholdMs = 5000;

// context: https://prextech.slack.com/archives/C07DN09JTUY/p1774614693786529
function getOutdatedMessageSkew({ p }: ServerMessageJsonValue): {
  diffMs: number;
  serverTimeMs: number;
  clientTimeMs: number;
} | null {
  const serverTimeMs = timestampToDate(p).getTime();
  const clientTimeMs = nearWorldClock.now();
  const diffMs = Math.abs(serverTimeMs - clientTimeMs);
  if (diffMs <= outdatedMessageThresholdMs) return null;
  return { diffMs, serverTimeMs, clientTimeMs };
}
